/** @jsxImportSource theme-ui */
import { createUser, editUser, fetchUsers, toggleUserActive } from 'admin/admin.saga';
import * as AdminSelectors from 'admin/admin.selectors';
import { FormikProps } from 'formik';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, DropdownItemProps } from 'semantic-ui-react';
import * as Yup from 'yup';

import AdminApi from 'api/AdminApi';
import * as dto from 'client/dto';
import { GeolocationFM } from 'models/FormModels';
import { triggerCsvDownload } from 'utils/downloadHelper';
import { nameof } from 'utils/typeHelpers';
import { PhoneRegExp } from 'utils/validationPatterns';

import { BdIcon } from 'components/_common/BdIcon';
import { FlexGroup } from 'components/_common/FlexGroup';
import BdFormLabel, {
  BdFormSection,
} from 'components/SidebarView/SettingsView/_common/BdFormLabel';
import { handleCitySearch as handleSettingsCitySearch } from 'components/SidebarView/SettingsView/_common/SettingsFormHelpers';
import { FormikSemanticCheckbox } from 'components/SidebarView/SettingsView/FormikControls/FormikSemanticCheckbox';
import { FormikSemanticDropDown } from 'components/SidebarView/SettingsView/FormikControls/FormikSemanticDropDown';
import { FormikSemanticInput } from 'components/SidebarView/SettingsView/FormikControls/FormikSemanticInput';
import { FormikSemanticServerAutocomplete } from 'components/SidebarView/SettingsView/FormikControls/FormikSemanticServerAutocomplete';

import * as UserSelectors from 'user/user.selectors';

import { SimpleCrudTable } from '../_common/SimpleCrudTable';
import ResetPasswordSection from './ResetPasswordSection';
import { ResendConfirmationEmailButton } from './UserAdmin.ResendConfirmation';
import { FormikSemanticUserAdminUsernameInput } from './UserAdmin.UsernameField';

// =====================================================================================================================================
const AlsoDisableExistingAccountFieldsWhenUserConflicts = true;
const UseManualFillOutExistingButton = false;
const ShowResendConfirmationEmailButton = true;
// =====================================================================================================================================

type UserAdminFM = {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  organization: string;
  phoneNumber: string;
  locationGeonameId: GeolocationFM;
  roleId: string;
  userGroupId: number;
  isDoNotTrack: boolean;
  isEmailConfirmed: boolean;
  isUsernameConflicting: boolean;
  isExternal: boolean;
};

const UserAdminFMValidationSchema = Yup.object().shape<Partial<UserAdminFM>>({
  firstName: Yup.string()
    .required('First Name is required')
    .max(256, 'First Name cannot be longer than 256 characters'),
  lastName: Yup.string()
    .required('Last Name is required')
    .max(256, 'Last Name cannot be longer than 256 characters'),
  email: Yup.string()
    .email('The Email field is not a valid e-mail address')
    .required('Email is required')
    .max(256, 'Email cannot be longer than 256 characters'),
  phoneNumber: Yup.string().nullable().matches(PhoneRegExp, 'Phone number is not valid'),
  roleId: Yup.string().required('Role selection is required'),
  organization: Yup.string().max(400, 'Organization cannot be longer than 400 characters'),
  locationGeonameId: Yup.object<GeolocationFM>().nullable().required('City selection is required'),
  isUsernameConflicting: Yup.bool().oneOf([true], 'Username already exist!'),
});

// =====================================================================================================================================

type RenderFormikUserAdminProps = {
  mode: 'create' | 'edit';
  formikProps: FormikProps<UserAdminFM>;
};

const RenderFormikUserAdmin: React.FC<RenderFormikUserAdminProps> = ({
  mode,
  formikProps: { initialValues, values, touched, errors, setFieldValue },
}) => {
  const roles = useSelector(UserSelectors.rolesSelector);
  const groups = useSelector(UserSelectors.groupsSelector);

  const [existingUserCheckModel, setExistingUserCheckModel] = useState<dto.UserModel>(null);
  const setIsUsernameConflictingField = useCallback(
    flag => setFieldValue(nameof<UserAdminFM>('isUsernameConflicting'), flag),
    []
  );

  const roleOptions = useMemo(
    () =>
      roles &&
      roles.map(
        x =>
          ({
            text: x.name || 'Select a role',
            value: x.id,
          } as DropdownItemProps)
      ),
    [roles]
  );

  const groupOptions = useMemo(
    () =>
      groups &&
      groups.map(
        x =>
          ({
            text: x.name || 'Select a group',
            value: x.id,
          } as DropdownItemProps)
      ),
    [groups]
  );

  React.useMemo(
    () =>
      groupOptions.push({
        text: 'Select a group',
        value: null,
      } as DropdownItemProps),
    [groupOptions]
  );

  const isExistingAccount = useMemo(
    () => existingUserCheckModel && existingUserCheckModel.isExistingAccount,
    [existingUserCheckModel]
  );

  const disableExistingAccountFields =
    isExistingAccount ||
    (AlsoDisableExistingAccountFieldsWhenUserConflicts && !values.isUsernameConflicting);

  function fillOutFormFromExistingUserCheckModel() {
    setFieldValue(nameof<UserAdminFM>('id'), existingUserCheckModel.id);
    setFieldValue(
      nameof<UserAdminFM>('firstName'),
      existingUserCheckModel.personalDetails && existingUserCheckModel.personalDetails.firstName
    );
    setFieldValue(
      nameof<UserAdminFM>('lastName'),
      existingUserCheckModel.personalDetails && existingUserCheckModel.personalDetails.lastName
    );
    setFieldValue(
      nameof<UserAdminFM>('organization'),
      existingUserCheckModel.personalDetails && existingUserCheckModel.personalDetails.organization
    );
    setFieldValue(
      nameof<UserAdminFM>('phoneNumber'),
      existingUserCheckModel.personalDetails && existingUserCheckModel.personalDetails.phoneNumber
    );
  }

  useEffect(() => {
    isExistingAccount && !UseManualFillOutExistingButton && fillOutFormFromExistingUserCheckModel();
  }, [isExistingAccount]);

  return (
    <>
      <BdFormLabel>Email</BdFormLabel>
      <FlexGroup
        suffix={
          isExistingAccount &&
          UseManualFillOutExistingButton && (
            <Button onClick={() => fillOutFormFromExistingUserCheckModel()}>
              Fill out existing info
            </Button>
          )
        }
      >
        <FormikSemanticUserAdminUsernameInput
          placeholder="Email"
          name={nameof<UserAdminFM>('email')}
          error={(touched.email && !!errors.email) || !!errors.isUsernameConflicting}
          disabled={mode === 'edit'}
          onUsernameConflictingChanged={mode === 'create' && setIsUsernameConflictingField}
          onUserModelCheckResultChanged={mode === 'create' && setExistingUserCheckModel}
        />
      </FlexGroup>
      <BdFormLabel>First Name</BdFormLabel>
      <FormikSemanticInput
        placeholder="FirstName"
        name={nameof<UserAdminFM>('firstName')}
        error={touched.firstName && !!errors.firstName}
        disabled={disableExistingAccountFields}
      />
      <BdFormLabel>Last Name</BdFormLabel>
      <FormikSemanticInput
        placeholder="LastName"
        name={nameof<UserAdminFM>('lastName')}
        error={touched.lastName && !!errors.lastName}
        disabled={disableExistingAccountFields}
      />
      <BdFormLabel>Organization</BdFormLabel>
      <FormikSemanticInput
        placeholder="Organization"
        name={nameof<UserAdminFM>('organization')}
        error={touched.organization && !!errors.organization}
        disabled={disableExistingAccountFields}
      />
      <BdFormLabel>Location (City)</BdFormLabel>
      <FormikSemanticServerAutocomplete
        placeholder="Toronto, Ontario, Canada"
        name={nameof<UserAdminFM>('locationGeonameId')}
        onSearch={handleSettingsCitySearch}
        error={touched.locationGeonameId && !!errors.locationGeonameId}
        forceFirstFetch
      />
      <BdFormLabel>Phone</BdFormLabel>
      <FormikSemanticInput
        placeholder="+1234561234"
        name={nameof<UserAdminFM>('phoneNumber')}
        error={touched.phoneNumber && !!errors.phoneNumber}
        disabled={disableExistingAccountFields}
      />
      <BdFormLabel>User Type</BdFormLabel>
      <FormikSemanticDropDown
        fluid
        name={nameof<UserAdminFM>('roleId')}
        options={roleOptions || []}
        error={touched.roleId && !!errors.roleId}
      />
      {mode === 'edit' && (
        <>
          <BdFormLabel>User Group</BdFormLabel>
          <FormikSemanticDropDown
            fluid
            name={nameof<UserAdminFM>('userGroupId')}
            options={groupOptions || []}
            error={touched.userGroupId && !!errors.userGroupId}
            sx={{
              '&.ui.dropdown .menu': {
                maxHeight: '328px',
              },
            }}
          />
          <BdFormSection>
            <FormikSemanticCheckbox
              name={nameof<UserAdminFM>('isDoNotTrack')}
              placeholder="Do NOT track"
              disabled
            />
          </BdFormSection>
          <BdFormSection>
            <FormikSemanticCheckbox
              name={nameof<UserAdminFM>('isEmailConfirmed')}
              placeholder="Email confirmed"
              disabled
            />
          </BdFormSection>
          {ShowResendConfirmationEmailButton && (
            <BdFormSection>
              <ResendConfirmationEmailButton
                userId={initialValues.id}
                disabled={initialValues.isEmailConfirmed}
              />
            </BdFormSection>
          )}
          <ResetPasswordSection userId={initialValues?.id} isExternal={initialValues?.isExternal} />
        </>
      )}
    </>
  );
};

// =====================================================================================================================================

const UserAdmin: React.FC = () => {
  const dispatch = useDispatch();
  const isLoading = useSelector(AdminSelectors.isAdminLoadingSelector);
  const dataList = useSelector(AdminSelectors.usersAlphabetical);

  useEffect(() => {
    dispatch(fetchUsers());
  }, []);

  const handleItemCreate = useCallback((item: UserAdminFM) => {
    dispatch(
      createUser({
        id: item.id,
        email: item.email,
        firstName: item.firstName,
        lastName: item.lastName,
        locationGeonameId: item.locationGeonameId && item.locationGeonameId.value,
        organization: item.organization,
        phoneNumber: item.phoneNumber,
        userTypeId: item.roleId,
      })
    );
  }, []);

  const handleItemEdit = useCallback((item: UserAdminFM) => {
    dispatch(
      editUser({
        id: item.id,
        data: {
          id: item.id,
          email: item.email,
          firstName: item.firstName,
          lastName: item.lastName,
          locationGeonameId: item.locationGeonameId && item.locationGeonameId.value,
          organization: item.organization,
          phoneNumber: item.phoneNumber,
          userTypeId: item.roleId,
          groupId: item.userGroupId,
        },
      })
    );
  }, []);

  const handleItemActivateToggle = useCallback((item: dto.UserModel) => {
    dispatch(toggleUserActive(item));
  }, []);

  const [isExporting, setIsExporting] = useState(false);

  const handleItemExport = useCallback(() => {
    setIsExporting(true);
    return AdminApi.exportUsers()
      .then(file => {
        triggerCsvDownload(file);
      })
      .finally(() => setIsExporting(false));
  }, []);

  return (
    <div>
      <h1>UserAdmin Page</h1>

      <SimpleCrudTable<dto.UserModel, UserAdminFM>
        loading={isLoading}
        list={dataList}
        entityType="User"
        columns={[
          { name: 'Id', getter: x => x.id },
          { name: 'UserName', getter: x => x.userName },
          // { name: 'Email', getter: x => x.personalDetails.email },
          { name: 'FirstName', getter: x => x.personalDetails && x.personalDetails.firstName },
          { name: 'LastName', getter: x => x.personalDetails && x.personalDetails.lastName },
          {
            name: 'Organization',
            getter: x => x.personalDetails && x.personalDetails.organization,
          },
          { name: 'PhoneNumber', getter: x => x.personalDetails && x.personalDetails.phoneNumber },
          { name: 'Location', getter: x => x.location && x.location.fullDisplayName },
          { name: 'Active', getter: x => x.isActive && <BdIcon name="icon-checkmark" /> },
        ]}
        keyGetter={x => x.id}
        rowDisabledGetter={x => !x.isActive}
        createNew={{
          title: 'Create New User',
          validationSchema: UserAdminFMValidationSchema,
          renderForm: formikProps => (
            <RenderFormikUserAdmin mode="create" formikProps={formikProps} />
          ),
          onSubmitForm: handleItemCreate,
          seed: {
            isUsernameConflicting: true,
          },
        }}
        exportToCsv={{
          title: 'Export to csv',
          isDisabled: isExporting,
          onExport: handleItemExport,
        }}
        actions={[
          {
            type: 'custom',
            actionText: 'Edit',
            custom: {
              isDisabled: item => !item.isActive,
              renderTitle: item => `Edit user "${item.userName}"`,
              validationSchema: UserAdminFMValidationSchema,
              renderForm: formikProps => (
                <RenderFormikUserAdmin mode="edit" formikProps={formikProps} />
              ),
              onSubmitForm: handleItemEdit,
              mapToFormData: (item: dto.UserModel) =>
                ({
                  isUsernameConflicting: true,
                  id: item.id,
                  email: item.personalDetails.email,
                  firstName: item.personalDetails.firstName,
                  lastName: item.personalDetails.lastName,
                  organization: item.personalDetails.organization,
                  phoneNumber: item.personalDetails.phoneNumber,
                  locationGeonameId:
                    (item.location && {
                      value: item.location.geonameId,
                      title: item.location.fullDisplayName,
                    }) ||
                    null,
                  roleId: item.userType && item.userType.id,
                  userGroupId: item.groupId,
                  isDoNotTrack: item.isDoNotTrack,
                  isEmailConfirmed: item.isEmailConfirmed,
                  isExternal: item.isExternal,
                } as UserAdminFM),
            },
          },
          {
            type: 'confirm',
            actionText: item => (item.isActive ? 'Deactivate' : 'Reactivate'),
            confirm: {
              render: (item: dto.UserModel) =>
                `Are you sure you want to ${item.isActive ? 'deactivate' : 'reactivate'} ${
                  item.userName
                }?`,
              onSubmit: handleItemActivateToggle,
            },
          },
        ]}
      />
    </div>
  );
};

export default UserAdmin;
